import styled from '@emotion/styled'
import { ReactNode } from 'react'
import { FlexboxProps, flexbox } from 'styled-system'

import type { BaseTextProps } from './Text'
import { theme } from '../constants'
import { mediaBreakpointGte } from '../utils'
import {
  SpaceProps,
  TypographyProps,
  ZIndexProps,
  fontSize as baseFontSize,
  space,
  typography,
  zIndex,
} from '../utils/styled-system'

const fontSize = (
  size: HeadingSize | [HeadingSize, HeadingSize],
  { mobile = true }: { mobile?: boolean },
) => {
  const toFontSize = (headingSize: HeadingSize) => {
    return getFontSize(headingSize, { mobile })
  }

  return baseFontSize({
    fontSize: Array.isArray(size) ? size.map(toFontSize) : toFontSize(size),
  })
}

const getFontSize = (size: HeadingSize, { mobile }: { mobile: boolean }) => {
  return mobile && size < 6
    ? theme.font.size[(size + 1) as HeadingSize]
    : theme.font.size[size]
}

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

export type HeadingProps = BaseTextProps &
  FlexboxProps &
  SpaceProps &
  TypographyProps &
  ZIndexProps & {
    children?: ReactNode
    className?: string
    color?: string
    display?: 'inline'
    setDefaultDisplay?: boolean
    level: HeadingSize
    size?: HeadingSize | [HeadingSize, HeadingSize]
    wide?: boolean
    minWidth?: number
  }

export type HeadingSize = 0 | HeadingLevel

/**
 * NOTE:
 * The level is not the same as the size, even though the level decides the size by default.
 * The heading level can be used for SEO purposes, while the size is purely cosmetic.
 *
 * It is recommended to have exactly one <h1> level element per page.
 */
export const Heading = (props: HeadingProps) => {
  const size = props.size ?? props.level

  const HeadingElement = styled(`h${props.level || 1}`)({
    alignItems: 'flex-start',
    color: props.color,
    display:
      props.display ?? (props.setDefaultDisplay !== false ? 'flex' : undefined),
    flexDirection: 'column',
    ...flexbox(props),
    ...fontSize(size, { mobile: true }),
    lineHeight: 1.3,
    maxWidth: props.wide ? 840 : 600,
    marginBottom: 20,
    marginTop: 0,
    ...(props.level === 6
      ? {
          fontSize: 13,
          fontWeight: theme.font.weight.bold,
          letterSpacing: '0.03rem',
          marginBottom: 8,
          textTransform: 'uppercase',
        }
      : {}),
    minWidth: props.minWidth,
    ...space(props),
    ...typography(props),
    ...zIndex(props),
    userSelect: props.noSelect ? 'none' : undefined,
    [mediaBreakpointGte('md')]: {
      ...fontSize(size, { mobile: false }),
    },
  })

  return (
    <HeadingElement className={props.className}>
      {props.children}
    </HeadingElement>
  )
}
